import React, { useState, useRef, useEffect } from "react";
import Tree from "react-d3-tree";
import "../assets/custom_tree.css";
import { Grid2 as Grid, LinearProgress } from "@mui/material";

const countVisibleNodes = (node, expandedNodes) => {
  if (!node) return 0;
  let count = 1; // Count this node

  if (node.children && expandedNodes.has(node.id)) {
    node.children.forEach((child) => {
      count += countVisibleNodes(child, expandedNodes);
    });
  }

  return count;
};

const computeMaxVisibleDepth = (node, expandedNodes, currentDepth = 0) => {
  let maxDepth = currentDepth;
  if (node.children && expandedNodes.has(node.id)) {
    node.children.forEach((child) => {
      maxDepth = Math.max(
        maxDepth,
        computeMaxVisibleDepth(child, expandedNodes, currentDepth + 1)
      );
    });
  }
  return maxDepth;
};

export default function ClosedLoopTree({ data }) {
  const treeContainer = useRef(null);
  const containerRef = useRef(null);
  const [dimension, setDimension] = useState({ width: 200, height: 200 });
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [key, setKey] = useState(1);

  const handleToggle = (nodeDatum) => {
    const newExpandedNodes = new Set(expandedNodes);
    if (newExpandedNodes.has(nodeDatum.id)) {
      newExpandedNodes.delete(nodeDatum.id); // Collapse
    } else {
      newExpandedNodes.add(nodeDatum.id); // Expand
    }

    if (nodeDatum?.__rd3t?.depth === 0 && !nodeDatum?.__rd3t?.collapsed) {
      newExpandedNodes.clear();
      setKey((prev) => prev + 1);
    }

    const visibleNodes = countVisibleNodes(data, newExpandedNodes);
    const maxDepth = computeMaxVisibleDepth(data, newExpandedNodes, 0);
    const calculatedHeight = Math.max(200, visibleNodes * 70);
    const calculatedWidth = Math.max(200, maxDepth * 200 + 250);

    setDimension({ width: calculatedWidth, height: calculatedHeight });
    setExpandedNodes(newExpandedNodes);
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = dimension.width;
    }
  }, [dimension.width]);

  const renderCustomNode = ({ nodeDatum, toggleNode }) => (
    <g
      style={{ cursor: "default" }}
    >
      <foreignObject
        x={-100}
        y={-7}
        width="200"
        height="200"
        requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
      >
        <LinearProgress
          variant="determinate"
          sx={{ width: 200, height: 15, cursor: "pointer" }}
          value={
            !nodeDatum.parent
              ? 100
              : Math.round((nodeDatum.value / nodeDatum.parent) * 100)
          }
          onClick={() => {
            handleToggle(nodeDatum);
            toggleNode();
          }}
        />

        <p
          xmlns="http://www.w3.org/1999/xhtml"
          className="font-bold "
          style={{ cursor: "pointer" }}
          onClick={() => {
            toggleNode();
            handleToggle(nodeDatum);
          }}
        >
          {nodeDatum.name}
        </p>
        {nodeDatum.value && (
          <p xmlns="http://www.w3.org/1999/xhtml">
            {`${nodeDatum.value} Mn IDR ${
              nodeDatum.parent
                ? `(${Math.round((nodeDatum.value / nodeDatum.parent) * 100)}%)`
                : ``
            }`}
          </p>
        )}
        {nodeDatum.transactions && (
          <p xmlns="http://www.w3.org/1999/xhtml">
            {`${nodeDatum.transactions} transactions `}
          </p>
        )}
      </foreignObject>
    </g>
  );

  const customPathFunc = (linkDatum, orientation) => {
    const { source, target } = linkDatum;
    if (orientation === "horizontal") {
      // For a horizontal layout, the parent's connection is shifted to the right (source.y + NODE_OFFSET)
      // and the child's connection is shifted to the left (target.y - NODE_OFFSET)
      const startX = source.x;
      const startY = source.y + 100; // right edge of parent's rect (x="-100", width="200")
      const endX = target.x;
      const endY = target.y - 100; // left edge of child's rect
      // Control points offset: adjust the horizontal positions to create the curve.
      const controlOffset = 80;
      const cp1Y = startY + controlOffset;
      const cp2Y = endY - controlOffset;
      return `M${startY},${startX} C${cp1Y},${startX} ${cp2Y},${endX} ${endY},${endX}`;
    } else {
      // For vertical layouts, adjust the x coordinates instead.
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        height: "60vh",
        overflow: "auto",
      }}
    >
      <div
        key={key}
        id="treeWrapper"
        ref={treeContainer}
        style={{
          width: dimension.width,
          height: dimension.height,
        }}
        className="mt-2"
      >
        <Tree
          key={1}
          data={data}
          collapsible={true}
          horizontal="horizontal"
          zoom={0.7}
          renderCustomNodeElement={renderCustomNode}
          dimensions={
            dimension.height === 200 ? { height: 200, width: 200 } : undefined
          }
          pathFunc={customPathFunc}
          translate={{ x: 100, y: dimension.height / 2 }}
          nodeSize={{ x: 300, y: 120 }}
          initialDepth={0}
          zoomable={false}
          draggable={false}
        />
      </div>
    </div>
  );
}
